import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Search, Filter, FolderTree, Globe } from "lucide-react"
import { searchInputOverlayIconClass } from "@/lib/searchInputIcon"
import { buildCollectionPath } from "../utils/collectionPaths"
import { buildCollectionSearchNavigationState } from "./searchNavigationState"
import type { MenuItemsModel, SearchPanelRow } from "./useMenuItemsModel"
import type { ComponentWithSource } from "../api/componentsData"
import { isConnectorBackedCollection } from "./collectionMenuGrouping"
import { CollectionMenuIcon } from "./CollectionMenuIcon"
import {
  collectionMenuPurpleIconClasses,
  collectionMenuTrailingIconSlotClass,
} from "./collectionNavRowClassNames"

type PickM = Pick<
  MenuItemsModel,
  | "searchTerm"
  | "handleSearchChange"
  | "clearSearch"
  | "handleSearchKeyDown"
  | "showComponentsNav"
  | "isPendingSetup"
  | "flattenedItems"
  | "menuSearchHits"
  | "searchPanelRows"
  | "itemsLoading"
  | "itemsError"
  | "itemSelectedIndex"
  | "filteredComponents"
  | "components"
  | "navigate"
  | "onNav"
  | "onCollectionMenuNavigate"
  | "isCollectionNavListButNoView"
>

function resultRowButtonClass(selected: boolean): string {
  const base =
    "w-full flex flex-col text-left px-2.5 py-2 rounded-md text-xs transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900"
  if (selected) {
    return `${base} bg-blue-100 dark:bg-blue-950/80 text-gray-900 dark:text-gray-50 shadow-[inset_3px_0_0_0] shadow-blue-600 dark:shadow-blue-400 ring-1 ring-blue-500/90 dark:ring-blue-400/90 z-[1] relative font-medium`
  }
  return `${base} text-gray-700 dark:text-gray-300 border border-transparent hover:bg-gray-100 dark:hover:bg-gray-700/90`
}

export function MenuItemsSearchPanel({
  m,
  mobile,
}: {
  m: PickM
  mobile: boolean
}) {
  const {
    searchTerm,
    handleSearchChange,
    clearSearch,
    handleSearchKeyDown,
    showComponentsNav,
    isPendingSetup,
    menuSearchHits,
    searchPanelRows,
    itemsLoading,
    itemsError,
    itemSelectedIndex,
    filteredComponents,
    components,
    navigate,
    onNav,
    onCollectionMenuNavigate,
    isCollectionNavListButNoView,
  } = m

  if (!showComponentsNav || isPendingSetup) {
    return null
  }

  const termTrimmed = searchTerm.trim()
  const showNoResultsHint =
    termTrimmed && !itemsLoading && !itemsError && searchPanelRows.length === 0

  const singleTableMatch = filteredComponents.length === 1 ? filteredComponents[0] : null

  const renderRow = (row: SearchPanelRow, index: number) => {
    const isSelected = itemSelectedIndex === index
    const cls = resultRowButtonClass(isSelected)

    if (row.type === "menu") {
      return (
        <button
          key={row.key}
          type="button"
          aria-selected={isSelected}
          onClick={() => {
            row.onActivate()
          }}
          className={cls}
        >
          <div className="flex items-center gap-2 min-w-0">
            <FolderTree className="w-3.5 h-3.5 shrink-0 text-violet-500 dark:text-violet-400" aria-hidden />
            <span className="truncate font-medium">{row.title}</span>
          </div>
          {row.subtitle ? (
            <div className="text-[0.7rem] text-gray-600 dark:text-gray-400 truncate pl-5 mt-0.5 font-normal">
              {row.subtitle}
            </div>
          ) : null}
        </button>
      )
    }

    if (row.type === "collection") {
      const c: ComponentWithSource = row.component
      const listNoView = isCollectionNavListButNoView(c)
      return (
        <button
          key={row.key}
          type="button"
          aria-selected={isSelected}
          title={listNoView ? "You do not have permission to view this table" : undefined}
          onClick={() => onCollectionMenuNavigate(c)}
          className={clsListNoView(cls, listNoView)}
        >
          <div className="flex items-center justify-between gap-2 min-w-0">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <CollectionMenuIcon
                menuIcon={c.menuIcon}
                className={`w-3.5 h-3.5 shrink-0 ${collectionMenuPurpleIconClasses}`}
              />
              <span className="truncate">{c.name || c.slug || "Table"}</span>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {isSelected ? (
                <span className="shrink-0 text-[0.65rem] uppercase tracking-wide text-blue-700 dark:text-blue-300">
                  Enter
                </span>
              ) : null}
              {isConnectorBackedCollection(c) ? (
                <span className={collectionMenuTrailingIconSlotClass} aria-label="Remote collection">
                  <Globe className={`w-3.5 h-3.5 ${collectionMenuPurpleIconClasses}`} />
                </span>
              ) : null}
            </div>
          </div>
          <div className="text-[0.7rem] text-gray-600 dark:text-gray-400 pl-5 mt-0.5 font-normal">
            {listNoView ? "No view access" : "Open table — single match"}
          </div>
        </button>
      )
    }

    const rawSlug =
      typeof (row.col.slug || row.col.collection) === "string" ? row.col.slug || row.col.collection : ""
    const slugValue = rawSlug.replace(/[!]+$/, "").trim()
    const fieldLabel = row.entry.matchingField || "field"
    const valueLabel = row.entry.matchingValue || ""
    return (
      <button
        key={row.key}
        type="button"
        aria-selected={isSelected}
        onClick={() => {
          if (!slugValue) return
          const highlightUid = row.entry.ref || row.entry.item?.uid || null
          navigate(buildCollectionPath({ slug: slugValue }), {
            state: buildCollectionSearchNavigationState({
              highlightUid,
              matchingField: row.entry.matchingField,
              matchingValue: row.entry.matchingValue,
              searchQuery: searchTerm,
            }),
          })
        }}
        className={cls}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="truncate font-medium">{row.col.title || row.col.slug || row.col.collection}</span>
          {isSelected ? (
            <span className="shrink-0 text-[0.65rem] uppercase tracking-wide text-blue-700 dark:text-blue-300">
              Enter
            </span>
          ) : null}
        </div>
        <div className="text-[0.7rem] text-gray-600 dark:text-gray-400 truncate mt-0.5 font-normal">
          {fieldLabel}
          {valueLabel && `: ${valueLabel}`}
        </div>
      </button>
    )
  }

  return (
    <div className={mobile ? "space-y-2" : "p-4 border-b border-gray-100 dark:border-gray-700 flex-none"}>
      <div className="relative flex items-center">
        <Search className={searchInputOverlayIconClass("left-3", "text-gray-400 z-10")} />
        <Input
          type="text"
          placeholder={mobile ? "Search menu & tables..." : "Search menu, tables, row content..."}
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={mobile ? undefined : handleSearchKeyDown}
          className="pl-10 pr-12 h-10 text-sm border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:border-blue-500 focus:ring-blue-500"
        />
        {searchTerm ? (
          <button
            type="button"
            onClick={clearSearch}
            className="absolute right-2 w-8 h-8 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all duration-200"
          >
            <span className="text-xl font-normal leading-none">×</span>
          </button>
        ) : null}
      </div>
      {!mobile ? (
        <div className="mt-2 text-xs text-gray-400 dark:text-gray-500 flex flex-col gap-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span>Tip:</span>
            <span className="inline-flex items-center rounded border border-gray-200 dark:border-gray-600 px-1.5 py-0.5 text-[0.7rem] font-medium bg-white/50 dark:bg-gray-800/60">
              /
            </span>
            <span>to focus search · arrows &amp; Enter on results</span>
          </div>
          {singleTableMatch && termTrimmed ? (
            <p className="text-[0.7rem] text-blue-700/90 dark:text-blue-300/90">
              One table matches this filter — it is selected; press Enter to open.
            </p>
          ) : null}
        </div>
      ) : null}
      {searchTerm ? (
        <div className="mt-2 space-y-2">
          {itemsLoading ? (
            <div className="text-xs text-gray-500 dark:text-gray-400">Searching table rows...</div>
          ) : null}
          {itemsError && !itemsLoading ? (
            <div className="text-xs text-red-500 dark:text-red-400">{itemsError}</div>
          ) : null}
          {searchPanelRows.length > 0 ? (
            <div className="space-y-1">
              <div className="text-[0.7rem] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Results
              </div>
              <div className="space-y-1 max-h-52 overflow-y-scroll scrollbar-gutter-stable rounded-md border border-gray-200/80 dark:border-gray-600/50 p-0.5 bg-gray-50/50 dark:bg-gray-900/30">
                {searchPanelRows.map((row, index) => renderRow(row, index))}
              </div>
            </div>
          ) : null}
          {showNoResultsHint ? (
            <div className="text-xs text-gray-500 dark:text-gray-400">No matches in menu or row search.</div>
          ) : null}
        </div>
      ) : null}
      {searchTerm ? (
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
          <Filter className="w-3 h-3 text-gray-400 shrink-0" />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {menuSearchHits.length} menu
            {menuSearchHits.length !== 1 ? " places" : " place"}
            {m.flattenedItems.length > 0
              ? ` · ${m.flattenedItems.length} row${m.flattenedItems.length !== 1 ? "s" : ""}`
              : ""}
            {" · "}
            {filteredComponents.length} of {components.length} tables
          </span>
          {filteredComponents.length !== components.length ? (
            <Badge variant="secondary" className="text-xs">
              Filtered
            </Badge>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function clsListNoView(base: string, listNoView: boolean): string {
  if (!listNoView) return base
  return `${base} opacity-70 cursor-not-allowed`
}
